// Plain <a href="/api/...csv"> links can't carry the JWT Authorization header,
// so hitting an authenticated export endpoint that way always 401s. This
// fetches with auth, then triggers a real file download from the blob.
/**
 * Open an authenticated file in a new tab (guest ID proofs — they live
 * behind GET /api/id-proofs/:filename now, so a plain <a href> gets a 401:
 * a new tab can't carry the Authorization header).
 *
 * The tab MUST be opened synchronously inside the click handler, before
 * the await — opening it after the fetch resolves puts it outside the user
 * gesture and every popup blocker kills it.
 *
 * It must NOT be opened with the "noopener" feature. The spec makes
 * window.open return null when noopener is set — the tab still opens, but
 * we get no handle to it. That was the bug: every click opened a blank tab
 * we couldn't reach, then fell through to the "popup blocked" branch and
 * navigated the admin's OWN tab to the blob. The opener link is severed by
 * hand below instead, which keeps the protection without losing the handle.
 */
export async function openWithAuth(url, authHeaders) {
  const tab = window.open("", "_blank");
  if (tab) {
    tab.opener = null;
    // A holding message rather than a bare white page while the file loads.
    try {
      tab.document.title = "Loading…";
      tab.document.body.innerHTML =
        '<p style="font:14px system-ui,sans-serif;color:#6b7280;padding:24px">Loading ID proof…</p>';
    } catch { /* cross-origin or already navigated — harmless */ }
  }

  try {
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) {
      let message = `Could not open file (${res.status})`;
      try { message = (await res.json()).error || message; } catch {}
      throw new Error(message);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);

    if (tab) {
      tab.location.href = objectUrl;
    } else {
      // Genuinely blocked by the browser. Never navigate the admin's own tab
      // away from the app to show it — download it instead, which needs no
      // popup permission and leaves them exactly where they were.
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = match ? match[1] : (url.split("/").pop() || "id-proof");
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    // Not revoked immediately: the new tab still needs to load from it.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}

export async function downloadWithAuth(url, authHeaders, fallbackFilename) {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    let message = `Download failed (${res.status})`;
    try { message = (await res.json()).error || message; } catch {}
    throw new Error(message);
  }
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackFilename;

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
